/**
 * Public memorial — music link card (featured list item or external platform link).
 */
import React, { useState } from 'react';
import { ExternalLink, Music, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import {
  getMusicPlatformLabel,
  isDirectAudioUrl,
  MUSIC_EXTERNAL_NOTE,
  MUSIC_PLATFORM_CONFIG,
} from '../../utils/musicLink';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { getBackendBaseUrl } from '../../utils/apiBase';

const BACKEND = getBackendBaseUrl();

export function MusicLinkCard({ track, className, compact = false }) {
  const [showEmbed, setShowEmbed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = React.useRef(null);

  if (!track?.link_url) return null;

  const platform = track.platform || 'other';
  const cfg = MUSIC_PLATFORM_CONFIG[platform] || MUSIC_PLATFORM_CONFIG.other;
  const playback = track.playback || (isDirectAudioUrl(track.link_url) ? 'audio' : 'external');
  const canEmbed = track.embeddable && track.embed_url && ['youtube', 'spotify', 'soundcloud'].includes(playback);

  const toggleAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().catch(() => setPlaying(false));
      setPlaying(true);
    }
  };

  const src = track.link_url.startsWith('http')
    ? track.link_url
    : resolveAssetUrl(BACKEND, track.link_url);

  return (
    <article
      className={cn(
        'overflow-hidden rounded-2xl border border-[hsl(38_14%_82%)] bg-[hsl(42_28%_99%)] shadow-[0_8px_28px_-18px_hsl(28_14%_18%_/_0.18)]',
        compact ? 'p-4' : 'p-5 sm:p-6',
        className,
      )}
      data-testid="music-link-card"
    >
      <div className="flex items-start gap-3">
        <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-semibold', cfg.markClass)}>
          {cfg.mark}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="font-serif text-base font-medium leading-snug text-[hsl(24_14%_16%)]">
            {track.title || 'Song'}
          </h4>
          {track.artist ? (
            <p className="text-sm text-[hsl(32_10%_42%)]">{track.artist}</p>
          ) : (
            <p className="text-xs text-[hsl(32_10%_46%)]">{getMusicPlatformLabel(platform)}</p>
          )}
          {track.note ? (
            <p className="mt-2 text-sm italic leading-relaxed text-[hsl(32_10%_38%)]">{track.note}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {playback === 'audio' ? (
          <Button type="button" variant="outline" size="sm" className="rounded-full border-[var(--memorial-gold,#c9a961)]/40" onClick={toggleAudio}>
            <Play className="mr-1.5 h-3.5 w-3.5" />
            {playing ? 'Pause' : 'Play'}
          </Button>
        ) : canEmbed ? (
          <Button type="button" variant="outline" size="sm" className="rounded-full border-[var(--memorial-gold,#c9a961)]/40" onClick={() => setShowEmbed((v) => !v)}>
            <Music className="mr-1.5 h-3.5 w-3.5" />
            {showEmbed ? 'Hide preview' : 'Play memorial soundtrack'}
          </Button>
        ) : null}
        <Button type="button" size="sm" className="h-9 rounded-full bg-[#8B7355] px-4 text-xs text-white hover:bg-[#7A6548]" asChild>
          <a href={track.link_url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Open song
          </a>
        </Button>
      </div>

      {playback === 'audio' ? (
        <audio ref={audioRef} src={src} preload="none" className="sr-only" onEnded={() => setPlaying(false)} onPause={() => setPlaying(false)} />
      ) : null}

      {showEmbed && canEmbed ? (
        <div className="mt-4 overflow-hidden rounded-xl border border-[hsl(38_14%_86%)]">
          <iframe
            title={track.title || 'Music preview'}
            src={track.embed_url}
            className="aspect-video w-full min-h-[152px]"
            allow="accelerometer; encrypted-media; picture-in-picture"
            loading="lazy"
          />
        </div>
      ) : null}

      <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground/80">{MUSIC_EXTERNAL_NOTE}</p>
    </article>
  );
}

export default MusicLinkCard;
